import { useState, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import { formValueSelector, getFormValues } from 'redux-form'
import _ from 'lodash'
import clx from 'classnames'
import { Modal, Tip } from '@vzmi/shp_shared_compo'
import { COMMA } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'
import { delay } from '@vzmi/component_ec_nodejs_util/dist/lib/renderUtil'
import { UPDATE_LISTING_FORM_KEY } from '../../../../../constants/proposalTypes'
import { removeNotNumberAndComma } from '../../../../../lib/parser'
import { RENDER_ID } from '../../../UpdateListing/helpers/converters'
import SpecSortModal from './Modal/SpecSort'
import NewComplimentariesModal, {
  NEW_COMPLIMENTARIES_FORM,
  FORM_INIT as NEW_COMPLIMENTARIES_FORM_INIT,
  FIELD as NEW_COMPLIMENTARIES_FIELD
} from './Modal/NewComplimentaries'
import ImportRuleModal from './Modal/ImportRule'
import {
  FORM,
  FORM_SECTION,
  TABLE_TYPE,
  TABLE_TYPE_RELATIVE_FIELDS,
  TABLE_GROUP_TYPE,
  TABLE_GROUP_BY_GROUP_SKU,
  COLLECT_KEY,
  GROUP_SKU_KEY,
  UPDATE_COLLECT_KEY,
  COLUMN,
  safeRequest,
  getSortedSpecs,
  getExtraModelsFieldsFromForm,
  getGroupFirstIndex
} from './helper'
import { getListingParamsFromForm } from '../helpers'
import styles from './index.css'

export const openPortal = ({ className, ...customProps }) => Modal.portal({
  noIcon: true,
  className: clx(
    styles.portal,
    { [className]: className },
    { [styles.withoutTitle]: _.isEmpty(customProps.title) }
  ),
  ...customProps
})

const TableActions = props => {
  const {
    dispatch,
    actions,
    change,
    readOnly,
    title,
    tableType,
    listing: originListing = {},
    listingParams,
    targetComponent: Component,
    shortTitleFeatureToggle
  } = props
  const {
    [tableType]: complimentaries = [],
    [FORM[tableType]]: fieldValues,
    [TABLE_GROUP_TYPE[tableType]]: complimentaryGroups,
    [TABLE_GROUP_BY_GROUP_SKU[tableType]]: groupMapByGroupSku,
    [COLUMN.SELECT_COMPLIMENTARY_MIN_QTY]: selectComplimentaryMinQty
  } = originListing
  const [specSortModalVisible, setSpecSortModalVisible] = useState(false)
  const [newComplimentariesModalVisible, setNewComplimentariesModalVisible] = useState(false)
  const [importRuleModalVisible, setImportRuleModalVisible] = useState(false)
  const animeClear = useRef(false)
  const debounceChange = useRef({})

  const isDataEmpty = _.isEmpty(complimentaries)

  const tableData = useMemo(() => {
    return _.map(complimentaries, (complimentary = {}, index) => {
      const {
        [GROUP_SKU_KEY]: groupSku
      } = complimentary
      const isNoAttr = _.isUndefined(groupSku)
      let newCollection
      if (!isNoAttr) {
        // 預設從最新的對應 group 找 groupName 當 collection 分類，如果沒有則從預設資料拿
        newCollection = _.get(groupMapByGroupSku, `${groupSku}.${COLUMN.GROUP_NAME}`, '')
      }
      return {
        index,
        isNoAttr,
        ...(!_.isUndefined(newCollection) && { [COLLECT_KEY]: newCollection }),
        // 動畫跑完後 animeClear 會讓之後重新 render 時不再有 update highlight key
        ..._.omit(complimentary, animeClear.current ? [UPDATE_COLLECT_KEY] : [])
      }
    })
  }, [complimentaries, groupMapByGroupSku])

  const tableDataGroupByGroupSku = useMemo(() => {
    return _.groupBy(tableData, GROUP_SKU_KEY)
  }, [tableData])

  // 判斷使否同時有無屬性、有屬性
  const isMultipleAttrType = useMemo(() => {
    return _.flow(
      () => _.keyBy(complimentaries, GROUP_SKU_KEY),
      grouped => {
        const keys = _.keys(grouped)
        // 分群後如果有無屬性贈品會分到 undefined, 有屬性則會有 groupkey xxx
        const isNoAttrExist = _.includes(keys, 'undefined')
        const isMultipleKey = (_.size(keys) > 1)
        // 同時有 undefined 且有有屬性才會有的 groupkey 即代表同時存在無屬性及有屬性
        return isNoAttrExist && isMultipleKey
      }
    )()
  }, [complimentaries])

  // 有屬性的顯示名稱選項
  const displayNameOptions = useMemo(() => {
    return _.flow(
      // 不包含無屬性
      () => _.filter(complimentaries, complimentary => !_.isUndefined(complimentary[GROUP_SKU_KEY])),
      filteredComplimentaries => _.map(filteredComplimentaries, filteredComplimentary => {
        const groupSku = _.get(filteredComplimentary, GROUP_SKU_KEY)
        const groupName = _.get(groupMapByGroupSku, `${groupSku}.${COLUMN.GROUP_NAME}`, '')
        const option = {
          value: groupSku,
          label: groupName
        }
        return option
      }),
      options => _.uniqBy(options, option => option.value)
    )()
  }, [complimentaries, groupMapByGroupSku])

  // 判斷有屬性時是否有多個 specs
  const isMultipleSpecs = useMemo(() => {
    return (_.size(displayNameOptions) > 1)
  }, [displayNameOptions])

  // specs 排序同時包含無屬性、有屬性
  const sortedSpecs = useMemo(() => {
    const newSortedSpecs = getSortedSpecs(complimentaries, groupMapByGroupSku)
    return newSortedSpecs
  }, [complimentaries, groupMapByGroupSku])

  // 修改無屬性名稱同步原始資料欄位
  const onFieldChange = (e, value, prevValue, fieldName) => {
    clearTimeout(debounceChange.current[fieldName])
    const findColumn = shortTitleFeatureToggle ? [COLUMN.ATTR_NAME, COLUMN.PRICE, COLUMN.SHORT_TITLE] : [COLUMN.ATTR_NAME, COLUMN.PRICE]
    const targetField = _.find(
      findColumn,
      field => _.endsWith(fieldName, field)
    )
    // listing.complimentariesFieldValues.0.spec.selectedValue
    // convert to x(relative renderId to row index)
    // listing.complimentaries[x].spec.selectedValue
    const rowId = _.replace(fieldName, `.${targetField}`, '')
    const convertedField = _.flow(
      () => _.get(props, `${rowId}.${RENDER_ID}`, ''),
      renderId => _.findIndex(complimentaries, complimentary => {
        return complimentary[RENDER_ID] === renderId
      }),
      rowIndex => {
        if (rowIndex === -1) {
          return ''
        }

        return `${FORM_SECTION}.${tableType}[${rowIndex}].${targetField}`
      }
    )()
    if (_.isEmpty(convertedField)) {
      console.error(`rowId: ${rowId} not found.`, { complimentaries })
      return
    }

    debounceChange.current[fieldName] = setTimeout(() => {
      change(convertedField, value)
    }, 300)
  }

  // 有屬性選擇 spec
  const onSelectCollect = rowId => async (e, newCollect, prevCollect, fieldName) => {
    const rowValues = _.get(originListing, rowId, {})
    const { [RENDER_ID]: rowRenderId } = rowValues
    // 選擇一樣時不觸發重新分群
    if (prevCollect === newCollect) {
      return
    }

    animeClear.current = false
    // 選擇不一樣時更新 spec 且加上 highlight 標記
    const newRowValues = {
      ...rowValues,
      [GROUP_SKU_KEY]: newCollect,
      [UPDATE_COLLECT_KEY]: true
    }
    // 扣掉修改列其他列加上 not highlight 標記
    const complimentariesWithoutCurrentRow = _.reduce(complimentaries, (newList, complimentary) => {
      if (complimentary[RENDER_ID] === rowRenderId) {
        return newList
      }

      newList.push({ ...complimentary, [UPDATE_COLLECT_KEY]: false })
      return newList
    }, [])
    // 找出重新分群後是新的群或是既有分群
    const targetCollectLastIndex = _.findLastIndex(complimentariesWithoutCurrentRow, complimentary => {
      const currentCollect = _.get(complimentary, GROUP_SKU_KEY, '')
      return currentCollect === newCollect
    })
    let newComplimentaries = []
    if (targetCollectLastIndex === -1) {
      // 新分群放到最後一列
      newComplimentaries = [
        ...complimentariesWithoutCurrentRow,
        newRowValues
      ]
    } else {
      // 既有分群插入該分群最後一筆
      const insertIndex = targetCollectLastIndex + 1
      newComplimentaries = [
        ..._.slice(complimentariesWithoutCurrentRow, 0, insertIndex),
        newRowValues,
        ..._.slice(complimentariesWithoutCurrentRow, insertIndex)
      ]
    }
    newComplimentaries = _.map(newComplimentaries, (newComplimentary, index) => {
      return { ...newComplimentary, index }
    })
    change(`${FORM_SECTION}.${tableType}`, newComplimentaries)
    // 如果新選的 spec 為自訂選項不在對應的 group 裡就要新增對應自訂選項
    const isCustomSpec = !(newCollect in groupMapByGroupSku)
    if (isCustomSpec) {
      const newGroup = [
        ...complimentaryGroups,
        {
          [COLUMN.GROUP_NAME]: newCollect,
          [GROUP_SKU_KEY]: newCollect
        }
      ]
      const newGroupMap = _.keyBy(newGroup, GROUP_SKU_KEY)
      change(`${FORM_SECTION}.${TABLE_GROUP_TYPE[tableType]}`, newGroup)
      change(`${FORM_SECTION}.${TABLE_GROUP_BY_GROUP_SKU[tableType]}`, newGroupMap)
    }

    await delay(700)
    animeClear.current = true
  }

  // 打開 specs 視窗
  const onOpenSpecSortModal = () => setSpecSortModalVisible(true)

  // 打開新增資料視窗
  const onOpenNewComplimentariesModal = () => {
    // 重置新增表單資料
    change(`${FORM_SECTION}.${NEW_COMPLIMENTARIES_FORM}`, NEW_COMPLIMENTARIES_FORM_INIT)
    setNewComplimentariesModalVisible(true)
  }

  // 打開匯入資料視窗
  const openImportRuleModal = () => setImportRuleModalVisible(true)

  const getRemoveSingleRowContent = (record) => {
    const tableDataSize = _.size(tableData)
    const {
      isNoAttr,
      [RENDER_ID]: renderId,
      [GROUP_SKU_KEY]: groupSku
    } = record
    const groupFirstIndex = getGroupFirstIndex(tableData, groupSku)
    // 被刪除列正確的 row index 要用 uniq id renderId 從表格重新查詢避免因為順序調整過造成 index 不正確
    const recordIndex = _.findIndex(tableData, { [RENDER_ID]: renderId })
    const isGroupFirstRow = (groupFirstIndex === recordIndex)
    // 目前分群與下一筆分群不同 groupSku 代表沒有 extra model 在同一個分群裡
    // 即代表該分群只有一筆
    const isGroupOnlyOneRow = groupSku !== _.get(tableData, `${recordIndex + 1}.${GROUP_SKU_KEY}`)
    // 有屬性、第一筆被刪除且同分群不止一筆時
    if (!isNoAttr && isGroupFirstRow && !isGroupOnlyOneRow) {
      return '贈品移除後，贈品代表圖將被替換，你確定要從賣場移除該贈品？'
    }

    // 贈品任選且刪除前表格列數量等於必選數量
    if (
      tableType === TABLE_TYPE.SELECT_COMPLIMENTARIES &&
      tableDataSize === +selectComplimentaryMinQty
    ) {
      return (
        <Tip
          type='important'
          tag='span'
        >
          {`提醒您，在移除贈品後，剩餘的贈品種類將不足其必選數量(${selectComplimentaryMinQty})！`}
        </Tip>
      )
    }

    return '贈品移除後，復原需重新提案，你確定要從賣場移除該贈品？'
  }

  // 刪除單列
  const removeRow = record => openPortal({
    content: getRemoveSingleRowContent(record),
    okText: '確認移除贈品',
    onOk: () => {
      const { [RENDER_ID]: renderId } = record
      // 扣掉被刪除列剩下的資料列拿掉 highlight 標記，避免更新後重新觸發動畫
      const newComplimentaries = _.reduce(complimentaries, (newList, complimentary) => {
        if (complimentary[RENDER_ID] === renderId) {
          return newList
        }

        newList.push(_.omit(complimentary, [UPDATE_COLLECT_KEY]))
        return newList
      }, [])
      const newFieldValues = _.omit(fieldValues, [renderId])
      const sortedSpecMap = _.keyBy(getSortedSpecs(newComplimentaries, groupMapByGroupSku), 'spec')
      const newComplimentaryGroups = _.filter(complimentaryGroups, complimentaryGroup => {
        return (complimentaryGroup[COLUMN.GROUP_NAME] in sortedSpecMap)
      })
      const newComplimentaryGroupsMap = _.keyBy(newComplimentaryGroups, GROUP_SKU_KEY)
      // 刪除列後清除對應的資料
      change(FORM_SECTION, {
        ...originListing,
        [tableType]: newComplimentaries,
        [FORM[tableType]]: newFieldValues,
        [TABLE_GROUP_TYPE[tableType]]: newComplimentaryGroups,
        [TABLE_GROUP_BY_GROUP_SKU[tableType]]: newComplimentaryGroupsMap
      })
    }
  })

  // 刪除全部列
  const removeAllRows = () => openPortal({
    'data-role': `${tableType}ConfirmModal`,
    content: (
      <p>
        贈品移除後，復原需重新提案，你確定要從賣場移除所有
        <Tip
          type='important'
          tag='span'
        >
          {title}
        </Tip>
        ？
      </p>
    ),
    onOk: () => {
      // 找出對應買就送、任選 redux form 相關欄位
      const relativeFields = [
        ..._.get(TABLE_TYPE_RELATIVE_FIELDS, tableType, []),
        tableType,
        _.get(FORM, tableType, '')
      ]
      // 對應欄位根據目前資料類型轉換成對應的空值
      const resetFields = _.reduce(relativeFields, (fields, relativeField) => {
        const originValue = _.get(originListing, relativeField)
        let resetValue = null
        switch (true) {
          case _.isArray(originValue): {
            resetValue = []
            break
          }
          // isObject 會把 arrary 同樣判斷成 object 所以放 isArray 後面判斷
          case _.isObject(originValue): {
            resetValue = {}
            break
          }
          default: {
            break
          }
        }
        fields[relativeField] = resetValue
        return fields
      }, {})
      // listing 保留原始資料只清空相關欄位
      change(FORM_SECTION, { ...originListing, ...resetFields })
    }
  })

  const getImportExtraModelsParams = (tableType, formValues = {}) => {
    const {
      [NEW_COMPLIMENTARIES_FIELD.MODELS]: models = []
    } = formValues
    // 組出匯入編號，過濾掉重複的只查不一樣的編號
    const skuCandidates = _.flow(
      () => _.map(models, NEW_COMPLIMENTARIES_FIELD.IDS),
      idsStrList => _.join(idsStrList, COMMA),
      removeNotNumberAndComma,
      idsStr => _.split(idsStr, COMMA),
      _.uniq,
      _.compact
    )()
    let extraModelParams = {}
    switch (tableType) {
      case TABLE_TYPE.COMPLIMENTARIES:
      case TABLE_TYPE.SELECT_COMPLIMENTARIES:
        extraModelParams = { isGift: true }
        break
      case TABLE_TYPE.ADDITIONAL_PURCHASES:
        extraModelParams = { isAdditionalPurchases: true }
        break
      default:
        break
    }
    return {
      skuCandidates,
      dryrun: true,
      ...extraModelParams
    }
  }

  const fetchImportExtraModels = tableType => async (formValues) => {
    const params = {
      ...listingParams,
      ...getImportExtraModelsParams(tableType, formValues)
    }
    const reservice = actions.createListingModelProposals
    const result = await safeRequest(reservice, params)
    return result
  }

  return (
    <>
      <Component
        key='component'
        title={title}
        change={change}
        readOnly={readOnly}
        isDataEmpty={isDataEmpty}
        isMultipleAttrType={isMultipleAttrType}
        isMultipleSpecs={isMultipleSpecs}
        displayNameOptions={displayNameOptions}
        tableData={tableData}
        tableDataGroupByGroupSku={tableDataGroupByGroupSku}
        groupMapByGroupSku={groupMapByGroupSku}
        removeAllRows={removeAllRows}
        removeRow={removeRow}
        onFieldChange={onFieldChange}
        onSelectCollect={onSelectCollect}
        onOpenSpecSortModal={onOpenSpecSortModal}
        onOpenNewComplimentariesModal={onOpenNewComplimentariesModal}
        shortTitleFeatureToggle={shortTitleFeatureToggle}
      />
      <SpecSortModal
        key='specSortModal'
        change={change}
        readOnly={readOnly}
        tableType={tableType}
        title={title}
        visible={specSortModalVisible}
        setVisible={setSpecSortModalVisible}
        sortedSpecs={sortedSpecs}
        complimentaries={complimentaries}
        groupMapByGroupSku={groupMapByGroupSku}
      />
      <NewComplimentariesModal
        key='newComplimentariesModal'
        title={title}
        dispatch={dispatch}
        change={change}
        tableType={tableType}
        sortedSpecs={sortedSpecs}
        displayNameOptions={displayNameOptions}
        visible={newComplimentariesModalVisible}
        setVisible={setNewComplimentariesModalVisible}
        openImportRuleModal={openImportRuleModal}
        fetchImportExtraModels={fetchImportExtraModels(tableType)}
        shortTitleFeatureToggle={shortTitleFeatureToggle}
      />
      <ImportRuleModal
        key='importRuleModal'
        visible={importRuleModalVisible}
        setVisible={setImportRuleModalVisible}
        tableType={tableType}
      />
    </>
  )
}

const selector = formValueSelector(UPDATE_LISTING_FORM_KEY)
const withTableActions = (targetComponent, compProps) => {
  let extraModelsFields = {}
  return connect((state, props) => {
    const { tableType } = compProps
    const { change, readOnly } = props
    const form = getFormValues(UPDATE_LISTING_FORM_KEY)(state)
    const listing = selector(state, FORM_SECTION) || {}
    const listingParams = getListingParamsFromForm(form)
    // 確認、檢視頁資料不再變更，所以直接重新轉換一次
    // 修改頁則維持原狀
    const nextExtraModelsFields = readOnly ? getExtraModelsFieldsFromForm(form, tableType) : {}
    if (!_.isEqual(extraModelsFields, nextExtraModelsFields)) {
      extraModelsFields = nextExtraModelsFields
      change(FORM_SECTION, { ...listing, ...extraModelsFields })
    }
    const newProps = {
      ...props,
      tableType,
      listing: {
        ...listing,
        ...extraModelsFields
      },
      listingParams,
      targetComponent
    }
    return newProps
  })(TableActions)
}

export default withTableActions
